import {
  WorkflowsTableRow,
  WorkflowCreateFields,
  TriggerEvent,
} from '@/types/workflow'
import {
  ChangeStepAction,
  ChangeStepCredential,
  ChangeStepSubject,
  ChangeTaskRequiredOption,
  FieldMapping,
  SetStepAvailableFieldsPayload,
  SetStepFilterPayload,
  SetStepFiltersPayload,
  SetStepOptionPayload,
  SetStepOptionsPayload,
  TransformationStep,
  UpdateTaskAppRequestBody,
  WFCreateStep,
} from '@/types/WorkflowCreate/workflowCreate'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { DefaultCronExpression } from '@/constants/cron/cron'
import { WorkflowRunType, WorkflowStepType } from '@/constants/workflows/workflowCreate'
import { App } from '@/types/applications'
import { FilterCode } from '@/constants/filters/filters'

interface workflowInitialState {
  workflows: WorkflowsTableRow[]
  create: WorkflowCreateFields,
  lastUpdatedFilter: string | undefined
}

const filterFieldMappingsByStep = (
  fieldMappings: FieldMapping[],
  order?: number,
  appId?: string
) => {
  if (!(typeof order === 'number' || typeof appId === 'string')) {
    return fieldMappings;
  }

  return fieldMappings.filter(mapping => {
    const [destinationOrderRaw, destinationAppId] = mapping.destinationId.split(';');
    const parsedOrder = Number(destinationOrderRaw);
    const orderMatches =
      typeof order === 'number' && !Number.isNaN(parsedOrder) && parsedOrder === order;
    const appMatches =
      typeof appId === 'string' && destinationAppId === appId;

    if (typeof order === 'number' && typeof appId === 'string') {
      return !(orderMatches && appMatches);
    }
    if (typeof order === 'number') {
      return !orderMatches;
    }
    return !appMatches;
  });
};

const initialState: workflowInitialState = {
  workflows: [],
  create: {
    runType: WorkflowRunType.SCHEDULED,
    cron: DefaultCronExpression,
    timezone: 'PST',
    amPm: 'AM',
    executionStartDate: new Date().toISOString().split('T')[0], // Store as YYYY-MM-DD only
    executionStartHour: 12,
    executionStartMinute: 0,
    fieldMappings: [],
    steps: [],
    transformationConfigurations: [],
  },
  lastUpdatedFilter: undefined
}

const setWorkflows = (
  state: workflowInitialState = initialState,
  action: PayloadAction<WorkflowsTableRow[]>
) => {
  return {
    ...state,
    workflows: action.payload
  }
}

const setSteps = (
  state: workflowInitialState = initialState,
  action: PayloadAction<WFCreateStep[]>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      steps: action.payload
    }
  }
}

const addStep = (
  state: workflowInitialState = initialState,
  action: PayloadAction<WFCreateStep>
) => {
  const stepExists = state.create.steps.some(t =>
    t.id === action.payload.id &&
    t.order === action.payload.order
  )
  if (stepExists) {
    return state
  }
  return {
    ...state,
    create: {
      ...state.create,
      steps: [...state.create.steps, action.payload]
    }
  }
}

const addTransformationConfiguration = (
  state: workflowInitialState = initialState,
  action: PayloadAction<TransformationStep>
) => {
  const isSameConfiguration = (config: TransformationStep) =>
    config.taskOrder === action.payload.taskOrder &&
    config.stepName === action.payload.stepName;

  const configExists = state.create.transformationConfigurations.some(isSameConfiguration)

  if (configExists) {
    // Override existing configuration with same taskOrder
    return {
      ...state,
      create: {
        ...state.create,
        transformationConfigurations: state.create.transformationConfigurations.map(config =>
          isSameConfiguration(config) ? action.payload : config
        )
      }
    }
  }

  return {
    ...state,
    create: {
      ...state.create,
      transformationConfigurations: [
        ...state.create.transformationConfigurations,
        action.payload
      ]
    }
  }
}

const setTransformationConfigurations = (
  state: workflowInitialState = initialState,
  action: PayloadAction<TransformationStep[]>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      transformationConfigurations: action.payload
    }
  }
}

const removeStepByStepId = (
  state: workflowInitialState = initialState,
  action: PayloadAction<WFCreateStep>
) => {
  const filteredFieldMappings = filterFieldMappingsByStep(
    state.create.fieldMappings,
    action.payload.order,
    action.payload.id
  );
  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.filter(t =>
        !(t.id === action.payload.id && t.order === action.payload.order)
      ),
      transformationConfigurations: state.create.transformationConfigurations.filter(t =>
        t.taskOrder !== action.payload.order
      ),
      fieldMappings: filteredFieldMappings
    }
  }
}

const updateStepApp = (
  state: workflowInitialState = initialState,
  action: PayloadAction<UpdateTaskAppRequestBody>
) => {
  const updatedSteps: WFCreateStep[] = []

  const { oldAppId, order, newAppId } = action.payload;
  let targetOrder: number | undefined = order;
  const matchesTargetStep = (wfStep: WFCreateStep) => {
    const matchesId = wfStep.id === oldAppId;
    const matchesOrder = typeof order === "number" ? wfStep.order === order : true;
    return matchesId && matchesOrder;
  };

  const step = state.create.steps.find(matchesTargetStep)
  if (step && typeof targetOrder !== 'number') {
    targetOrder = step.order;
  }
  const otherSteps = state.create.steps.filter(stepItem => !matchesTargetStep(stepItem))

  if (step) {
    updatedSteps.push({
      id: newAppId,
      action: '',
      subject: '',
      credentialId: '',
      order: step.order,
      options: [],
      filters: [],
      requiredOptions: [],
    })
  }
  if (otherSteps) {
    updatedSteps.push(...otherSteps)
  }
  const filteredFieldMappings = filterFieldMappingsByStep(
    state.create.fieldMappings,
    targetOrder,
    oldAppId
  );
  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
      fieldMappings: filteredFieldMappings
    }
  }
}

const changeStepAction = (
  state: workflowInitialState = initialState,
  action: PayloadAction<ChangeStepAction>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.map(step =>
        step.id === action.payload.id && step.order === action.payload.order
          ? { ...step, action: action.payload.action }
          : step
      )
    }
  }
}

const changeStepSubject = (
  state: workflowInitialState = initialState,
  action: PayloadAction<ChangeStepSubject>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.map(step =>
        step.id === action.payload.id && step.order === action.payload.order
          ? { ...step, subject: action.payload.subject }
          : step
      )
    }
  }
}

const changeStepCredential = (
  state: workflowInitialState = initialState,
  action: PayloadAction<ChangeStepCredential>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.map(step =>
        step.id === action.payload.id && step.order === action.payload.order
          ? { ...step, credentialId: action.payload.credentialId }
          : step
      )
    }
  }
}

const setStepFilter = (
  state: workflowInitialState = initialState,
  action: PayloadAction<SetStepFilterPayload>
) => {
  const { stepId, order, filter, filterValue } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      const updatedFilters = [...step.filters];
      const existingFilterIndex = updatedFilters.findIndex(f => f.filter === filter);

      if (existingFilterIndex !== -1) {
        // Update existing filter
        updatedFilters[existingFilterIndex] = {
          ...updatedFilters[existingFilterIndex],
          filterValue
        };
      } else {
        // Add new filter
        updatedFilters.push({ filter, filterValue });
      }

      return {
        ...step,
        filters: updatedFilters,
        lastUpdatedFilter: filter
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    },
    lastUpdatedFilter: filter
  };
};

const removeStepFilter = (
  state: workflowInitialState = initialState,
  action: PayloadAction<{ stepId: string; order: number; filter: string }>
) => {
  const { stepId, order, filter } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      const updatedFilters = step.filters.filter(f => f.filter !== filter);

      return {
        ...step,
        filters: updatedFilters
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    }
  };
};


const setStepFilters = (
  state: workflowInitialState = initialState,
  action: PayloadAction<SetStepFiltersPayload>
) => {
  const { stepId, order, filters } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      if (filters.length === 0) {
        // clear filters completely
        return {
          ...step,
          filters: [],
          lastUpdatedFilter: undefined
        };
      }

      const updatedFilters = [...step.filters];

      filters.forEach(({ filter, filterValue }) => {
        const existingIndex = updatedFilters.findIndex(f => f.filter === filter);
        if (existingIndex !== -1) {
          updatedFilters[existingIndex] = {
            ...updatedFilters[existingIndex],
            filterValue
          };
        } else {
          updatedFilters.push({ filter, filterValue });
        }
      });

      return {
        ...step,
        filters: updatedFilters,
        lastUpdatedFilter: filters[filters.length - 1].filter
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    },
    lastUpdatedFilter: filters.length > 0 ? filters[filters.length - 1].filter : undefined
  };
};

const setStepOption = (
  state: workflowInitialState = initialState,
  action: PayloadAction<SetStepOptionPayload>
) => {
  const { stepId, order, option, optionValue } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      const updatedOptions = [...step.options];
      const existingOptionIndex = updatedOptions.findIndex(o => o.option === option);

      if (existingOptionIndex !== -1) {
        // Update existing filter
        updatedOptions[existingOptionIndex] = {
          ...updatedOptions[existingOptionIndex],
          optionValue
        };
      } else {
        // Add new filter
        updatedOptions.push({ option, optionValue });
      }

      return {
        ...step,
        options: updatedOptions,
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    },
  };
};

const removeStepOption = (
  state: workflowInitialState = initialState,
  action: PayloadAction<{ stepId: string; order: number; option: string }>
) => {
  const { stepId, order, option } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      const updatedOptions = step.options.filter(o => o.option !== option);

      return {
        ...step,
        options: updatedOptions
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    }
  };
};

const setStepOptions = (
  state: workflowInitialState = initialState,
  action: PayloadAction<SetStepOptionsPayload>
) => {
  const { stepId, order, options } = action.payload;

  const updatedSteps = state.create.steps.map(step => {
    if (step.id === stepId && step.order === order) {
      if (options.length === 0) {
        // Explicit reset of options
        return {
          ...step,
          options: []
        };
      }

      const updatedOptions = [...step.options];

      options.forEach(({ option, optionValue }) => {
        const existingIndex = updatedOptions.findIndex(o => o.option === option);
        if (existingIndex !== -1) {
          updatedOptions[existingIndex] = {
            ...updatedOptions[existingIndex],
            optionValue
          };
        } else {
          updatedOptions.push({ option, optionValue });
        }
      });

      return {
        ...step,
        options: updatedOptions,
      };
    }
    return step;
  });

  return {
    ...state,
    create: {
      ...state.create,
      steps: updatedSteps
    }
  };
};

const setStepAvailableFields = (
  state: workflowInitialState = initialState,
  action: PayloadAction<SetStepAvailableFieldsPayload>
) => {
  const { stepId, order, fields } = action.payload;

  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.map(step =>
        (step.id === stepId || stepId === WorkflowStepType.TRANSFORMATION) && step.order === order
          ? { ...step, availableFields: fields }
          : step
      )
    }
  };
};

const updateStepRequiredOptions = (
  state: workflowInitialState = initialState,
  action: PayloadAction<ChangeTaskRequiredOption>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      steps: state.create.steps.map(step =>
        step.id === action.payload.id
          ? { ...step, requiredOptions: action.payload.options }
          : step
      )
    }
  }
}

const addFieldMappings = (
  state: workflowInitialState = initialState,
  action: PayloadAction<FieldMapping[]>
) => {
  const updatedFieldMappings = [...state.create.fieldMappings];

  action.payload.forEach(newMapping => {
    const existingIndex = updatedFieldMappings.findIndex(
      map => map.destinationId === newMapping.destinationId
    );

    if (existingIndex !== -1) {
      updatedFieldMappings[existingIndex] = newMapping;
    } else {
      updatedFieldMappings.push(newMapping);
    }
  });

  return {
    ...state,
    create: {
      ...state.create,
      fieldMappings: updatedFieldMappings
    }
  };
}

const setFieldMappings = (
  state: workflowInitialState = initialState,
  action: PayloadAction<FieldMapping[]>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      fieldMappings: action.payload
    }
  }
}

const resetWorkflowCreate = (
  state: workflowInitialState = initialState
) => {
  return {
    ...state,
    create: {
      runType: WorkflowRunType.SCHEDULED,
      cron: DefaultCronExpression,
      timezone: 'PST',
      amPm: 'AM',
      executionStartDate: new Date().toISOString().split('T')[0], // Store as YYYY-MM-DD only
      executionStartHour: 12,
      executionStartMinute: 0,
      fieldMappings: [],
      steps: [],
      transformationConfigurations: [],
      availableFields: {}
    },
    selectedFilters: [],
    selectedOptions: [],
    lastUpdatedFilter: undefined,
  }
}

const setWorkflowRunType = (
  state: workflowInitialState = initialState,
  action: PayloadAction<WorkflowRunType>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      runType: action.payload
    }
  }
}

const setCron = (
  state: workflowInitialState = initialState,
  action: PayloadAction<string>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      cron: action.payload
    }
  }
}

const setTimezone = (
  state: workflowInitialState = initialState,
  action: PayloadAction<string>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      timezone: action.payload
    }
  }
}

const setAmPm = (
  state: workflowInitialState = initialState,
  action: PayloadAction<string>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      amPm: action.payload
    }
  }
}

const setExecutionStartDate = (
  state: workflowInitialState = initialState,
  action: PayloadAction<string>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      executionStartDate: action.payload
    }
  }
}

const setExecutionHour = (
  state: workflowInitialState = initialState,
  action: PayloadAction<number>
) => {
  // Normalize hour to valid 12-hour format (1-12)
  let normalizedHour = action.payload
  if (normalizedHour === 0) normalizedHour = 12
  if (normalizedHour < 1) normalizedHour = 1
  if (normalizedHour > 12) normalizedHour = 12

  return {
    ...state,
    create: {
      ...state.create,
      executionStartHour: normalizedHour
    }
  }
}

const setExecutionMinute = (
  state: workflowInitialState = initialState,
  action: PayloadAction<number>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      executionStartMinute: action.payload
    }
  }
}

const setSource = (
  state: workflowInitialState = initialState,
  action: PayloadAction<App | null>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      dataSource: action.payload
    }
  }
}

const setTriggerEvent = (
  state: workflowInitialState = initialState,
  action: PayloadAction<TriggerEvent>
) => {
  return {
    ...state,
    create: {
      ...state.create,
      triggerEvent: action.payload
    }
  }
}

const setLastUpdatedFilter = (
  state: workflowInitialState = initialState,
  action: PayloadAction<FilterCode | undefined>
) => {
  return {
    ...state,
    lastUpdatedFilter: action.payload,
  };
}

const reset = () => initialState

const workflowSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {
    setWorkflows,
    setWorkflowRunType,
    setCron,
    setTimezone,
    setAmPm,
    setExecutionStartDate,
    setExecutionHour,
    setExecutionMinute,
    setSource,
    setTriggerEvent,
    setSteps,
    changeStepAction,
    changeStepSubject,
    changeStepCredential,
    setStepFilter,
    removeStepFilter,
    setStepFilters,
    setStepOption,
    removeStepOption,
    setStepOptions,
    setStepAvailableFields,
    updateStepRequiredOptions,
    addStep,
    removeStepByStepId,
    addTransformationConfiguration,
    setTransformationConfigurations,
    updateStepApp,
    addFieldMappings,
    setFieldMappings,
    resetWorkflowCreate,
    setLastUpdatedFilter,
    reset
  }
})

export const workflowReducer = workflowSlice.reducer
export const workflowActions = workflowSlice.actions
